'use server'

import { apiRequest, ApiResponse } from '../request'

export type PriceTableType = 'CBHPM' | 'TUSS' | 'AMB' | 'CUSTOM'
export type PriceTableStatus = 'ACTIVE' | 'INACTIVE' | 'DRAFT'

export interface PriceTable {
    id: number
    companyId: number
    healthPlanId?: number
    healthPlan?: {
        id: number
        name: string
    }
    name: string
    type: PriceTableType
    version?: string
    status: PriceTableStatus
    validFrom?: string
    validUntil?: string
    baseMultiplier: number
    createdAt: string
    updatedAt: string
    _count?: {
        items: number
    }
}

export interface PriceTableItem {
    id: number
    priceTableId: number
    procedureCode: string
    procedureName: string
    category?: string
    baseValue: number
    coefficient: number
    finalValue: number
    auxCost?: number
    anesthesiaCost?: number
    filmCost?: number
    notes?: string
    createdAt: string
    updatedAt: string
}

export interface CreatePriceTableData {
    healthPlanId?: number
    name: string
    type: PriceTableType
    version?: string
    status?: PriceTableStatus
    validFrom?: string
    validUntil?: string
    baseMultiplier?: number
}

export interface UpdatePriceTableData extends Partial<CreatePriceTableData> {}

export interface CreatePriceTableItemData {
    procedureCode: string
    procedureName: string
    category?: string
    baseValue: number
    coefficient?: number
    auxCost?: number
    anesthesiaCost?: number
    filmCost?: number
    notes?: string
}

export interface UpdatePriceTableItemData
    extends Partial<CreatePriceTableItemData> {}

export interface PriceTableFilters {
    healthPlanId?: number
    type?: PriceTableType
    status?: PriceTableStatus
}

// Price Table CRUD
export async function getPriceTables(
    filters?: PriceTableFilters
): Promise<ApiResponse<PriceTable[]>> {
    return apiRequest<PriceTable[]>('billing/price-tables', { params: filters })
}

export async function getPriceTable(
    id: number
): Promise<ApiResponse<PriceTable>> {
    return apiRequest<PriceTable>(`billing/price-tables/${id}`, {})
}

export async function createPriceTable(
    data: CreatePriceTableData
): Promise<ApiResponse<PriceTable>> {
    return apiRequest<PriceTable>('billing/price-tables', { data }, 'post')
}

export async function updatePriceTable(
    id: number,
    data: UpdatePriceTableData
): Promise<ApiResponse<PriceTable>> {
    return apiRequest<PriceTable>(`billing/price-tables/${id}`, { data }, 'patch')
}

export async function deletePriceTable(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`billing/price-tables/${id}`, {}, 'delete')
}

export async function duplicatePriceTable(
    id: number,
    name: string
): Promise<ApiResponse<PriceTable>> {
    return apiRequest<PriceTable>(
        `billing/price-tables/${id}/duplicate`,
        { data: { name } },
        'post'
    )
}

// Price Table Items
export async function getPriceTableItems(
    priceTableId: number,
    filters?: { category?: string; search?: string }
): Promise<ApiResponse<PriceTableItem[]>> {
    return apiRequest<PriceTableItem[]>(
        `billing/price-tables/${priceTableId}/items`,
        { params: filters }
    )
}

export async function getPriceTableItem(
    priceTableId: number,
    itemId: number
): Promise<ApiResponse<PriceTableItem>> {
    return apiRequest<PriceTableItem>(
        `billing/price-tables/${priceTableId}/items/${itemId}`,
        {}
    )
}

export async function createPriceTableItem(
    priceTableId: number,
    data: CreatePriceTableItemData
): Promise<ApiResponse<PriceTableItem>> {
    return apiRequest<PriceTableItem>(
        `billing/price-tables/${priceTableId}/items`,
        { data },
        'post'
    )
}

export async function updatePriceTableItem(
    priceTableId: number,
    itemId: number,
    data: UpdatePriceTableItemData
): Promise<ApiResponse<PriceTableItem>> {
    return apiRequest<PriceTableItem>(
        `billing/price-tables/${priceTableId}/items/${itemId}`,
        { data },
        'patch'
    )
}

export async function deletePriceTableItem(
    priceTableId: number,
    itemId: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(
        `billing/price-tables/${priceTableId}/items/${itemId}`,
        {},
        'delete'
    )
}

export async function importPriceTableItems(
    priceTableId: number,
    items: CreatePriceTableItemData[]
): Promise<ApiResponse<{ imported: number }>> {
    return apiRequest<{ imported: number }>(
        `billing/price-tables/${priceTableId}/items/import`,
        { data: { items } },
        'post'
    )
}

// Lookup
export async function lookupOrderPrice(
    healthPlanId: number,
    procedureCode: string
): Promise<ApiResponse<PriceTableItem | null>> {
    return apiRequest<PriceTableItem | null>(
        `billing/price-tables/lookup/${healthPlanId}/${procedureCode}`,
        {}
    )
}

// Labels
export const PRICE_TABLE_TYPE_LABELS: Record<PriceTableType, string> = {
    CBHPM: 'CBHPM',
    TUSS: 'TUSS',
    AMB: 'AMB',
    CUSTOM: 'Personalizada',
}

export const PRICE_TABLE_STATUS_LABELS: Record<PriceTableStatus, string> = {
    ACTIVE: 'Ativa',
    INACTIVE: 'Inativa',
    DRAFT: 'Rascunho',
}

export const PRICE_TABLE_STATUS_COLORS: Record<PriceTableStatus, string> = {
    ACTIVE: 'badge-success',
    INACTIVE: 'badge-ghost',
    DRAFT: 'badge-warning',
}
